// src/lib/managerWeekShape.js
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  New. Split from managerWeek.js so it can be tested.
//
// WHICH OF HIS SIX WEEKLY ITEMS THE PRODUCT CAN SEE, AND WHICH IT ASKS ABOUT.
//
// Two of the six are countable and are never ticked by hand, because asking
// somebody to tick a box about a thing you already measured is how a checklist
// stops being believed.
//
//   index 0  "One recorded or live role-play per salesperson"
//            A practice drill in the coach is a rep, and activity_events has
//            them per person. Shown as X of Y people.
//
//   index 5  "Recognition of process behaviors, not only deliveries"
//            The floor board counts exactly this, and it exists because his
//            scorecard says to do it.
//
// The other four happen in a CRM or on a floor and are ticked. Marking them
// honestly as the manager's own record beats inventing a signal, which is the
// call managerDay.js already makes about the three daily disciplines that live
// in the CRM.
//
// Everything here is pure. See CLAUDE.md on the import rule.

import { dayKey, addDays } from './localDate.js'

// The indexes the product measures rather than asks about. Positions into
// `weeklyCoachingRhythm`, which is his list and could change: a test asserts
// these two still say what they are supposed to say.
export const MEASURED = { roleplay: 0, recognition: 5 }

// THE WEEK STARTS MONDAY, stored as that Monday's date so two managers in one
// store cannot disagree about which week it is. A dealership week is not a
// calendar week anywhere, and Monday is the least wrong.
export function mondayOf(d = new Date()) {
  const day = d.getDay() // 0 Sunday, 1 Monday
  const back = day === 0 ? 6 : day - 1
  return addDays(dayKey(d), -back)
}

export function weekLabel(weekStart) {
  const [y, m, dd] = weekStart.split('-').map(Number)
  const start = new Date(y, m - 1, dd)
  const end = new Date(y, m - 1, dd + 6)
  const fmt = (x, withYear) =>
    x.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) })
  return `${fmt(start, false)} to ${fmt(end, true)}`
}

export function isThisWeek(weekStart, now = new Date()) {
  return weekStart === mondayOf(now)
}

// Done counts the ticked ones PLUS the measured ones that genuinely happened,
// so a manager who ran drills with everybody is not asked to also tick it.
export function weekProgress({ items, total, roleplayDone, recognitionDone }) {
  const ticked = new Set(items || [])
  let done = 0
  for (let i = 0; i < total; i += 1) {
    if (i === MEASURED.roleplay) {
      if (roleplayDone) done += 1
    } else if (i === MEASURED.recognition) {
      if (recognitionDone) done += 1
    } else if (ticked.has(i)) {
      done += 1
    }
  }
  return { done, total }
}

export function toggle(items, index) {
  const set = new Set(items || [])
  if (set.has(index)) set.delete(index)
  else set.add(index)
  return [...set].sort((a, b) => a - b)
}

// A manager who got to four of six had a week, not a failure. The sentence
// reports and never grades, same as every other screen in the manager layer.
export function weekHeadline({ done, total }, floorSize) {
  if (!floorSize) return 'Nobody is on this floor yet.'
  if (done === 0) return 'Nothing recorded for this week yet.'
  if (done >= total) return 'The whole rhythm ran this week.'
  return `${done} of ${total} this week.`
}
